import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  IconButton,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import useBookingDescription from '../../hooks/useBookingDescription';
import { useBookingsContext } from '../../contexts/BookingsContext';

import { StyledSideTab, StyledSideTabs } from '../StyledTabs';
import { useHomeownerNotesStyles } from './styles';
import { COLORS } from '../../utils/colors';

interface IHomeownerNotesProps {
  useTabs: boolean;
  viewTitle: string;
  editButton?: boolean;
}

const HomeOwnerNotes = ({
  viewTitle,
  useTabs,
  editButton,
}: IHomeownerNotesProps) => {
  const styles = useHomeownerNotesStyles();
  const [editContext, setEditContext] = useState<boolean>(false);
  const [updateContext, setUpdateContext] = useState<string>('');
  const { contextRegex } = useBookingDescription();

  const {
    description,
    yearBuilt,
    purchaseDate,
    context,
    setContext,
    selectedBooking,
    updateDetails,
    updateDetailsLoading,
  } = useBookingsContext();

  useEffect(() => {
    setUpdateContext(context);
  }, [context]);

  const [value, setValue] = useState(0);

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  const calculateAge = () => {
    const currentDate = new Date();
    let age = currentDate.getFullYear() - parseInt(yearBuilt);
    return age;
  };

  const editSessionContext = async (state: boolean, save: boolean = false) => {
    if (!selectedBooking || !updateDetails) return;

    if (save && updateContext != context) {
      setContext(updateContext);

      await updateDetails({
        ...selectedBooking,
        description: `${selectedBooking.description.replace(contextRegex, '').trim()}\n\nContext: ${context}`,
      });
    }
    setUpdateContext(context);
    setEditContext(state);
  };

  return (
    <>
      <StyledSideTabs value={value} onChange={() => handleChange}>
        <StyledSideTab value={0} label={viewTitle} />
      </StyledSideTabs>
      <Box>
        <Grid sx={styles.notesPanel} container spacing={1}>
          <Typography
            fontSize={'13px'}
            sx={{ width: '100%', marginLeft: '0.5rem' }}>
            {yearBuilt && (
              <Box mb={4}>
                <Typography
                  variant="caption"
                  fontWeight={500}
                  fontSize={'14px'}>
                  Year Built
                </Typography>
                <Typography>
                  {yearBuilt !== ''
                    ? calculateAge() + ` Years Old. (Built in ${yearBuilt})`
                    : '[Year the home was built]'}
                </Typography>
              </Box>
            )}
            {purchaseDate && (
              <Box mb={4}>
                <Typography
                  variant="caption"
                  fontWeight={500}
                  fontSize={'14px'}>
                  Year Purchased
                </Typography>
                <Typography>
                  {purchaseDate !== ''
                    ? 'Since ' + purchaseDate
                    : '[Year the home was purchased]'}
                </Typography>
              </Box>
            )}
            <Box mb={5}>
              <Typography variant="caption" fontWeight={500} fontSize={'14px'}>
                Details
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-line' }}>
                {description.trim() !== '' ? description : 'No details added'}
              </Typography>
            </Box>

            {!useTabs && (
              <>
                <Box
                  sx={{
                    borderBottom: `1px solid ${COLORS.MEDIUM_GRAY}`,
                    mt: '2rem',
                    mb: '2rem',
                  }}></Box>
                <Box mt={2} mb={4}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    fontSize={'14px'}>
                    Template Type
                  </Typography>
                  <Typography>
                    {selectedBooking?.payment?.plan.label
                      ? selectedBooking?.payment?.plan.label
                      : '[Report template used]'}
                  </Typography>
                </Box>
                {(context || editContext) && (
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      fontSize={'14px'}>
                      Session Context
                    </Typography>
                    {!editContext && (
                      <Box display="flex" alignItems="center">
                        <Typography sx={{ whiteSpace: 'pre-line', flex: 1 }}>
                          {context}
                        </Typography>
                        {editButton && (
                          <IconButton
                            onClick={() => editSessionContext(true)}
                            sx={{ marginLeft: 'auto', marginBottom: 'auto' }}>
                            <Edit />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
            {editContext && (
              <ClickAwayListener
                onClickAway={() => {
                  console.log('click away...');
                  editSessionContext(false);
                }}>
                <div style={{ paddingTop: '1rem' }}>
                  <TextField
                    label="Enter your summary here"
                    variant="outlined"
                    value={updateContext}
                    onChange={(event: any) => {
                      setUpdateContext(event.target.value);
                    }}
                    fullWidth
                    multiline
                    rows={4}
                    sx={{
                      marginTop: '1rem',
                      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Box display={'flex'} justifyContent={'flex-end'}>
                    <Button
                      onClick={() => editSessionContext(false, true)}
                      variant="outlined"
                      sx={{ marginTop: '1rem' }}>
                      {updateDetailsLoading ? (
                        <CircularProgress size={'30px'} />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </div>
              </ClickAwayListener>
            )}
          </Typography>
        </Grid>
      </Box>
    </>
  );
};

export default HomeOwnerNotes;
